/**
 * Compile the month (the batch train's headline act; founder order
 * 2026-08-31: "we don't yet have batch processing, including batch
 * compiling. make this happen").
 *
 * NOT selection-driven, deliberately: a client with no invoice has no
 * row in the list to select. Candidates come from the unbilled report
 * the page already reads - rows whose reasons include 'not_invoiced'
 * are exactly "compile would work" (the unbilled repo computes that
 * with the same overlap predicate compile enforces), and rows with
 * 'invoice_already_issued' compile a second invoice for the month - and
 * the act says its whole plan UP FRONT: how many clients it will
 * compile, which of them get a second invoice, and how many
 * reconcile-blocked clients it will skip without attempting.
 *
 * The run is compileMonthSequentially: one POST per client through the
 * same per-client compile route the form beside this uses, strictly
 * sequential (compile serializes per client; issue's numbering needs the
 * batch sequential), with progress (n of m plus the client's name) and a
 * bucket report in the batch bar's report language.
 *
 * Demo clients are candidates like any other; the plan line says how
 * many of them there are. The clients with notes waiting and nothing
 * Ready are named before the run and in the report, never called. A
 * server error files that client as not confirmed and the run goes on;
 * a dead connection stops it. Every report list shows 20 names, then
 * "Show all {n} clients". Every count sentence names its noun ("client"
 * or "clients"). While a run is going, leaving the page asks first (the
 * calls in flight still finish, and a re-run is the recovery).
 */

import { type ReactElement, type ReactNode, useEffect, useState } from 'react';
import { useTenant } from '@/lib/auth';
import { useSupportContact } from '@/hooks/useSupportContact';
import { canCompileInvoice } from '@/lib/capabilities';
import type { UnbilledWorkWire } from '@/lib/api';
import {
  compileMonthSequentially,
  splitCompileCandidates,
  compileReasonSentence,
  batchStopSentence,
  type CompileMonthReport,
  type CompileOutcome,
  type NotReadyClient,
} from '@/lib/billingBatch';
import { Button } from '@/components/primitives/Button';

export interface CompileMonthActionProps {
  /** 'YYYY-MM', the page's picked month: the batch compiles THE month
   *  the operator is looking at, with its full calendar bounds. */
  month: string;
  /** The month's display name for the button ("August 2026"). */
  monthLabel: string;
  /** The unbilled report the page already queried; undefined while it
   *  is loading or the seat cannot read it, and the act stays absent. */
  unbilled: UnbilledWorkWire | undefined;
  /** Fired once the run settles, so every invoice read refreshes. */
  onDone: () => void;
}

/** A report list shows this many names, then "Show all {n} clients". */
const PAGE = 20;

/** The plan line names this many clients, then "and {k} more." */
const NAMED = 5;

/** "1 client" / "{n} clients". */
function clients(n: number): string {
  return n === 1 ? '1 client' : `${n} clients`;
}

/** The plan line for the clients whose month was already sent: they get
 *  a second invoice. Names in report order, the first five, then
 *  "and {k} more." */
function secondInvoiceLine(names: readonly string[], monthLabel: string): string {
  const shown = names.slice(0, NAMED).join('; ');
  const rest = names.length - NAMED;
  const list = rest > 0 ? `${shown}; and ${rest} more.` : `${shown}.`;
  return names.length === 1
    ? `1 client already has a sent ${monthLabel} invoice, so this client gets a second one: ${list}`
    : `${names.length} clients already have a sent ${monthLabel} invoice, so these clients get a second one: ${list}`;
}

/** A report bucket: its heading, an optional line under it, and its
 *  names, the first 20 until "Show all {n}" is pressed. */
function Bucket<T extends { client_id: string }>({
  testId,
  heading,
  items,
  explanation,
  renderItem,
}: {
  testId: string;
  heading: string;
  items: readonly T[];
  explanation?: string;
  renderItem: (item: T) => ReactNode;
}): ReactNode {
  const [all, setAll] = useState(false);
  const shown = all ? items : items.slice(0, PAGE);
  return (
    <div data-testid={testId} className="mt-2 space-y-1">
      <h4 className="text-xs font-semibold text-sage-800">{heading}</h4>
      {explanation !== undefined && <p className="text-xs text-ink-soft">{explanation}</p>}
      <ul className="space-y-1">
        {shown.map((item) => (
          <li key={item.client_id} data-testid={`${testId}-${item.client_id}`} className="text-xs text-ink-soft">
            {renderItem(item)}
          </li>
        ))}
      </ul>
      {!all && items.length > PAGE && (
        <Button variant="ghost" size="sm" data-testid={`${testId}-more`} onClick={() => setAll(true)}>
          {`Show all ${clients(items.length)}`}
        </Button>
      )}
    </div>
  );
}

function BucketList({
  testId,
  heading,
  rows,
  explanation,
}: {
  testId: string;
  heading: string;
  rows: CompileOutcome[];
  explanation?: string;
}): ReactNode {
  const contact = useSupportContact();
  return (
    <Bucket
      testId={testId}
      heading={heading}
      explanation={explanation}
      items={rows.map((o) => ({ ...o, client_id: o.candidate.client_id }))}
      renderItem={({ candidate, reason }) => (
        <>
          <span className="font-medium text-ink">{candidate.client_display_name}</span>{' '}
          {reason !== null ? compileReasonSentence(reason, contact) : ''}
        </>
      )}
    />
  );
}

/** "{name}: {N} notes still to be marked Ready before compiling." */
function notReadyLine(c: NotReadyClient): ReactNode {
  return (
    <>
      <span className="font-medium text-ink">{c.client_display_name}</span>
      {`: ${c.not_ready_count} ${c.not_ready_count === 1 ? 'note' : 'notes'} still to be marked Ready before compiling.`}
    </>
  );
}

/** Everything the compile-the-month act knows: the plan read from the
 *  unbilled report, the run, its progress and its report. The page that
 *  hosts it may place the button, the plan notes and the report apart
 *  (the Invoices page puts the button in its header band); the run is
 *  the same wherever its parts are drawn. */
export interface CompileMonthState {
  canCompile: boolean;
  monthLabel: string;
  candidates: ReturnType<typeof splitCompileCandidates>['candidates'];
  reconcileFirst: ReturnType<typeof splitCompileCandidates>['reconcileFirst'];
  notReady: ReturnType<typeof splitCompileCandidates>['notReady'];
  demoCount: number;
  secondNames: string[];
  running: boolean;
  progress: { done: number; total: number; name: string } | null;
  report: CompileMonthReport | null;
  /** False when there is nothing to compile, nothing to name and no
   *  report: the act is absent. */
  visible: boolean;
  run: () => Promise<void>;
}

export function useCompileMonth({
  month,
  monthLabel,
  unbilled,
  onDone,
}: CompileMonthActionProps): CompileMonthState {
  const { whoami, tenantSlug } = useTenant();
  const canCompile = canCompileInvoice(whoami);
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number; name: string } | null>(
    null,
  );
  const [report, setReport] = useState<CompileMonthReport | null>(null);

  // While a run is going, leaving the page asks first. The listener goes
  // when the run settles or the act unmounts.
  useEffect(() => {
    if (!running) return;
    const ask = (e: BeforeUnloadEvent): void => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', ask);
    return () => window.removeEventListener('beforeunload', ask);
  }, [running]);

  const rows = unbilled?.rows ?? [];
  const { candidates, reconcileFirst, notReady } = splitCompileCandidates(rows);
  const demoIds = new Set(rows.filter((r) => r.is_demo).map((r) => r.client_id));
  const demoCount = candidates.filter((c) => demoIds.has(c.client_id)).length;
  // The candidates whose month's invoice was sent: compile makes each a
  // second invoice. Report order.
  const sentIds = new Set(rows.filter((r) => r.reasons.includes('invoice_already_issued')).map((r) => r.client_id));
  const secondNames = candidates.filter((c) => sentIds.has(c.client_id)).map((c) => c.client_display_name);

  // Nothing to compile and nothing to name is nothing to offer: an act
  // that always reads "Compile for 0 clients" is furniture. The report
  // outlives the counts that produced it (the refetch after a clean run
  // empties the candidate list, and the report is the only place the
  // operator learns what happened).
  const visible =
    canCompile &&
    (candidates.length > 0 || reconcileFirst.length > 0 || notReady.length > 0 || report !== null);

  const run = async (): Promise<void> => {
    setRunning(true);
    setReport(null);
    setProgress({ done: 0, total: candidates.length, name: '' });
    const result = await compileMonthSequentially(tenantSlug, month, rows, {
      onProgress: (done, total, name) => setProgress({ done, total, name }),
    });
    setReport(result);
    setRunning(false);
    setProgress(null);
    onDone();
  };

  return {
    canCompile,
    monthLabel,
    candidates,
    reconcileFirst,
    notReady,
    demoCount,
    secondNames,
    running,
    progress,
    report,
    visible,
    run,
  };
}

/** The run button, present only while there is a client to compile. */
export function CompileMonthRunButton({
  state,
  onDark = false,
}: {
  state: CompileMonthState;
  /** On the forest band: the on-dark secondary (LP-39: Pre-billing and
   *  Payroll hours are the band's filled buttons). */
  onDark?: boolean;
}): ReactNode {
  if (!state.visible || state.candidates.length === 0) return null;
  const n = state.candidates.length;
  return (
    <Button
      size="sm"
      variant={onDark ? 'secondary-on-dark' : 'primary'}
      data-testid="compile-month-run"
      onClick={() => void state.run()}
      loading={state.running}
      disabled={state.running}
    >
      {`Compile ${state.monthLabel} for ${n} ${n === 1 ? 'client' : 'clients'}`}
    </Button>
  );
}

/** What the run will do, said before it runs, and its progress while it
 *  runs. Each note is one short line. */
export function CompileMonthNotes({
  state,
  showNotReady = true,
}: {
  state: CompileMonthState;
  /** The Invoices page counts these clients in its month line instead. */
  showNotReady?: boolean;
}): ReactNode[] {
  if (!state.visible) return [];
  const { demoCount, secondNames, reconcileFirst, notReady, running, progress, monthLabel } = state;
  return [
    demoCount > 0 && (
      <span key="demo" data-testid="compile-month-demo-note" className="text-xs text-ink-soft">
        {demoCount === 1
          ? '1 of these clients is a demo client.'
          : `${demoCount} of these clients are demo clients.`}
      </span>
    ),
    secondNames.length > 0 && (
      <span key="second" data-testid="compile-month-additional-note" className="text-xs text-ink-soft">
        {secondInvoiceLine(secondNames, monthLabel)}
      </span>
    ),
    reconcileFirst.length > 0 && (
      /* The skip is announced BEFORE the run: these clients bill
         from before Olive and can only 409. Skipped loudly, never
         called-and-failed. */
      <span key="skip" data-testid="compile-month-skip-note" className="text-xs text-ink-soft">
        {`${clients(reconcileFirst.length)} waiting on reconcile will be skipped.`}
      </span>
    ),
    showNotReady && notReady.length > 0 && (
      <span key="not-ready" data-testid="compile-month-not-ready-note" className="text-xs text-ink-soft">
        {`${clients(notReady.length)} not compiled: no ready notes.`}
      </span>
    ),
    running && progress && (
      <span key="progress" data-testid="compile-month-progress" className="text-xs text-ink-soft">
        {progress.name === ''
          ? `0 of ${clients(progress.total)}`
          : `${progress.done} of ${clients(progress.total)}: ${progress.name}`}
      </span>
    ),
  ].filter((note): note is ReactElement => note !== false && note !== null);
}

/** The report of the last run: one summary line, then each bucket.
 *  `collapsible` puts the buckets behind "See details", open from the
 *  start when a client did not compile or was not confirmed. */
export function CompileMonthReportView({
  report,
  collapsible = false,
}: {
  report: CompileMonthReport;
  collapsible?: boolean;
}): ReactNode {
  const buckets = (
    <>
      {report.compiled.length > 0 && (
        <BucketList
          testId="compile-month-compiled"
          heading={`${clients(report.compiled.length)} compiled`}
          rows={report.compiled}
          explanation="Each of these clients has a new draft invoice for the month, ready to review and approve."
        />
      )}

      {report.skipped_reconcile.length > 0 && (
        <BucketList
          testId="compile-month-skipped"
          heading={`${clients(report.skipped_reconcile.length)} waiting on reconcile`}
          rows={report.skipped_reconcile}
        />
      )}

      {report.already_invoiced.length > 0 && (
        <BucketList
          testId="compile-month-already"
          heading={`${clients(report.already_invoiced.length)} with an unsent invoice`}
          rows={report.already_invoiced}
        />
      )}

      {report.no_billable_events.length > 0 && (
        <BucketList
          testId="compile-month-empty"
          heading={`${clients(report.no_billable_events.length)} with nothing to invoice`}
          rows={report.no_billable_events}
        />
      )}

      {report.failed.length > 0 && (
        <BucketList
          testId="compile-month-failed"
          heading={`${clients(report.failed.length)} did not compile`}
          rows={report.failed}
        />
      )}

      {report.unconfirmed.length > 0 && (
        <BucketList
          testId="compile-month-unconfirmed"
          heading={`${clients(report.unconfirmed.length)} not confirmed: refresh the list to see whether each client's invoice was compiled.`}
          rows={report.unconfirmed}
        />
      )}

      {report.not_ready.length > 0 && (
        <Bucket
          testId="compile-month-not-ready"
          heading={`${clients(report.not_ready.length)} not compiled: no ready notes`}
          items={report.not_ready}
          renderItem={notReadyLine}
        />
      )}
    </>
  );
  return (
    <div data-testid="compile-month-report" className="space-y-1">
      <p data-testid="compile-month-summary" className="text-sm text-ink">
        {[
          report.compiled.length > 0 ? `${clients(report.compiled.length)} compiled.` : null,
          report.already_invoiced.length > 0
            ? `${clients(report.already_invoiced.length)} with an unsent invoice.`
            : null,
          report.no_billable_events.length > 0
            ? `${clients(report.no_billable_events.length)} with nothing to invoice.`
            : null,
          report.skipped_reconcile.length > 0
            ? `${clients(report.skipped_reconcile.length)} waiting on reconcile.`
            : null,
          report.failed.length > 0 ? `${clients(report.failed.length)} did not compile.` : null,
          report.unconfirmed.length > 0 ? `${clients(report.unconfirmed.length)} not confirmed.` : null,
          report.not_ready.length > 0
            ? `${clients(report.not_ready.length)} not compiled: no ready notes.`
            : null,
          report.stopped !== null ? 'Stopped early.' : null,
        ]
          .filter((clause) => clause !== null)
          .join(' ')}
      </p>

      {collapsible ? (
        <details
          data-testid="compile-month-details"
          open={report.failed.length > 0 || report.unconfirmed.length > 0}
        >
          <summary className="cursor-pointer text-xs font-semibold text-sage-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400">
            See details
          </summary>
          {buckets}
        </details>
      ) : (
        buckets
      )}

      {report.stopped !== null && (
        <p data-testid="compile-month-stopped" className="mt-2 text-xs text-pepper-deep">
          {batchStopSentence(report.stopped, 'compile')}
        </p>
      )}
    </div>
  );
}

export function CompileMonthAction(props: CompileMonthActionProps): ReactNode {
  const state = useCompileMonth(props);
  if (!state.visible) return null;
  const notes = CompileMonthNotes({ state });
  const button = CompileMonthRunButton({ state });
  return (
    <div data-testid="compile-month" className="space-y-2 pt-2 border-t border-sage-200">
      {(button !== null || notes.length > 0) && (
        <div className="flex flex-wrap items-center gap-3">
          {button}
          {notes}
        </div>
      )}
      {state.report && <CompileMonthReportView report={state.report} />}
    </div>
  );
}
